import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.HEP_AB,
  displayName: "Combined Hepatitis A and B",
  serviceType: "administration",
  minimumAgeYears: 1,
  indicationSummary:
    "At risk of both hepatitis A and B through travel to endemic areas, origin from a high-endemicity area, sexual behaviour (including MSM), or certain occupational exposures. Use where protection against both antigens is indicated.",

  brands: [
    {
      brandId: "twinrix-adult",
      brandName: "Twinrix Adult\u00ae",
      minimumAgeYears: 16,
      route: "IM",
      doseDescription: "1.0 mL per dose",
      administrationNotes:
        "Shake the pre-filled syringe to obtain a homogeneous slightly opaque white suspension. Inspect before use. Do not administer if the plunger of the pre-filled syringe detaches. Administer into the deltoid muscle. Do not use the gluteal region. Do not switch to another combined or single-antigen hepatitis vaccine mid-course.",
    },
    {
      brandId: "twinrix-paediatric",
      brandName: "Twinrix Paediatric\u00ae",
      minimumAgeYears: 1,
      maximumAgeYears: 15,
      route: "IM",
      doseDescription: "0.5 mL per dose",
      administrationNotes:
        "Shake the pre-filled syringe to obtain a homogeneous slightly opaque white suspension. Administer into the deltoid muscle. In infants and young children, administer into the anterolateral thigh. Do not switch brands or to single-antigen vaccines mid-course.",
    },
    {
      brandId: "ambirix",
      brandName: "Ambirix\u00ae",
      minimumAgeYears: 1,
      maximumAgeYears: 15,
      route: "IM",
      doseDescription: "1.0 mL per dose",
      administrationNotes:
        "Shake the pre-filled syringe to obtain a homogeneous slightly opaque white suspension. Administer into the deltoid muscle or anterolateral thigh. Ambirix\u00ae is a 2-dose schedule only \u2014 do not use to complete a course started with Twinrix Paediatric\u00ae or any single-antigen vaccine.",
    },
  ],

  schedules: [
    {
      scheduleId: "hep-ab-twinrix-adult-conventional",
      label: "Twinrix Adult\u00ae \u2014 conventional (0, 1 and 6 months)",
      offLabel: false,
      minimumAgeYears: 16,
      doses: [
        { doseNumber: 1, label: "Day 0" },
        {
          doseNumber: 2,
          label: "1 month after dose 1",
          intervalFromDose1Days: 30,
        },
        {
          doseNumber: 3,
          label: "6 months after dose 1",
          intervalFromDose1Days: 180,
        },
      ],
      completionNotes:
        "Do not switch brands or to single-antigen vaccines mid-course. A delayed dose does not require restarting the course from dose 1.",
    },
    {
      scheduleId: "hep-ab-twinrix-adult-accelerated",
      label:
        "Twinrix Adult\u00ae \u2014 accelerated (days 0, 7 and 21 + 12-month booster) \u2014 18 years and over, off-label",
      offLabel: true,
      offLabelRationale:
        "The Twinrix Adult\u00ae accelerated schedule (days 0, 7, and 21) is used outside the product licence. It is clinically established for adults aged 18 and over when travel is imminent and the conventional schedule cannot be completed. Use requires documented rationale and patient consent to off-label use. This schedule must not be used in patients under 18 years.",
      minimumAgeYears: 18,
      doses: [
        { doseNumber: 1, label: "Day 0" },
        {
          doseNumber: 2,
          label: "Day 7",
          intervalFromDose1Days: 7,
        },
        {
          doseNumber: 3,
          label: "Day 21",
          intervalFromDose1Days: 21,
        },
        {
          doseNumber: 4,
          label: "12-month booster (from dose 1)",
          intervalFromDose1Days: 365,
        },
      ],
      completionNotes:
        "The 12-month booster dose is essential for long-term protection, particularly against hepatitis B, and must be arranged before concluding this course. Off-label use \u2014 patient consent must be documented.",
    },
    {
      scheduleId: "hep-ab-twinrix-paediatric-conventional",
      label: "Twinrix Paediatric\u00ae \u2014 conventional (0, 1 and 6 months)",
      offLabel: false,
      minimumAgeYears: 1,
      maximumAgeYears: 15,
      doses: [
        { doseNumber: 1, label: "Day 0" },
        {
          doseNumber: 2,
          label: "1 month after dose 1",
          intervalFromDose1Days: 30,
        },
        {
          doseNumber: 3,
          label: "6 months after dose 1",
          intervalFromDose1Days: 180,
        },
      ],
      completionNotes:
        "Do not switch to Ambirix\u00ae or to single-antigen vaccines mid-course.",
    },
    {
      scheduleId: "hep-ab-ambirix",
      label:
        "Ambirix\u00ae \u2014 2-dose (0 and 6\u201312 months) \u2014 ages 1\u201315, low hepatitis B risk, compliance assured",
      offLabel: false,
      minimumAgeYears: 1,
      maximumAgeYears: 15,
      doses: [
        { doseNumber: 1, label: "Day 0" },
        {
          doseNumber: 2,
          label: "6\u201312 months after dose 1",
          intervalFromDose1Days: 180,
        },
      ],
      completionNotes:
        "Ambirix\u00ae provides a 2-dose schedule for patients aged 1\u201315 years at low risk of hepatitis B where adherence to the return appointment is assured. Hepatitis B protection is not established until after the second dose \u2014 advise the patient accordingly. Do not use to complete a course started with Twinrix Paediatric\u00ae or any single-antigen vaccine.",
    },
  ],

  capturesPriorDoseHistory: true,

  exclusions: [
    {
      checkId: "hep-ab-ex-anaphylaxis",
      question:
        "Has the patient had a confirmed anaphylactic reaction to a previous dose of any combined hepatitis A and B vaccine, or to any hepatitis A-containing or hepatitis B-containing vaccine, or to any of their components?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Combined hepatitis A and B vaccine is contraindicated. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "hep-ab-ex-neomycin",
      question:
        "Does the patient have known hypersensitivity to neomycin?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Combined hepatitis A and B vaccines contain neomycin residuals and are contraindicated in patients with neomycin hypersensitivity. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "hep-ab-ex-confirmed-immunity",
      question:
        "Has the patient been confirmed as immune to both hepatitis A and hepatitis B through documented natural infection or completion of a prior vaccination course?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Vaccination is not indicated where confirmed immunity to both antigens is already established. Do not proceed.",
      referralMessage:
        "Review the vaccination history. If immunity to only one antigen is confirmed, single-antigen vaccination may be appropriate \u2014 refer to the GP.",
    },
    {
      checkId: "hep-ab-ex-post-exposure",
      question:
        "Is this patient seeking post-exposure prophylaxis following a known or suspected exposure to hepatitis A or hepatitis B?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Post-exposure prophylaxis is outside this PGD. Do not supply under this PGD.",
      referralMessage:
        "Refer urgently to the patient's GP or an urgent care setting for post-exposure management, which may require specific immunoglobulin.",
    },
  ],

  cautions: [
    {
      checkId: "hep-ab-caut-bleeding",
      question:
        "Does the patient have a bleeding disorder or is the patient on anticoagulant therapy?",
      cautionMessage:
        "Administer by intramuscular injection with care using a fine-gauge needle with firm pressure applied for at least two minutes afterwards. For patients on stable warfarin with INR within therapeutic range, IM injection may proceed. Document the clinical decision.",
    },
    {
      checkId: "hep-ab-caut-pregnancy",
      question: "Is the patient pregnant or breastfeeding?",
      cautionMessage:
        "Both antigens are inactivated. Vaccination should proceed where the risk of hepatitis A or B infection is high and the benefit outweighs any theoretical risk. Advise the patient to inform their GP or midwife.",
    },
    {
      checkId: "hep-ab-caut-immunosuppressed",
      question: "Is the patient immunosuppressed due to disease or treatment?",
      cautionMessage:
        "Immunosuppression may reduce the immune response to both antigens. Vaccination should still proceed \u2014 both antigens are inactivated and safe. Counsel that protection may be suboptimal.",
    },
    {
      checkId: "hep-ab-caut-obesity",
      question:
        "Does the patient have a BMI of 30 or above? (Relevant if Twinrix Adult\u00ae is the selected product.)",
      cautionMessage:
        "Obesity (BMI \u226530) may reduce the immune response to Twinrix Adult\u00ae. Consider checking hepatitis B antibody titres 4\u20138 weeks after completing the course and arranging additional doses if seroconversion is suboptimal. Document the discussion.",
    },
    {
      checkId: "hep-ab-caut-syncope",
      question:
        "Is there any history of syncope (fainting) following a previous injection or vaccination?",
      cautionMessage:
        "Syncope can occur following vaccination. Ensure the patient is seated or lying down during and for the duration of the observation period after administration.",
    },
  ],

  supplyGates: [],

  requiresICVP: false,
  requiresLariamCard: false,
  requiresYellowCardDiscussion: false,
  requiresCHISNotificationUnder19: true,
  requiresGPNotification: true,
  standardObservationMinutes: 15,
  extendedObservationMinutes: 30,

  patientAdvicePoints: [
    "This vaccine protects against hepatitis A and hepatitis B only. It does not protect against hepatitis C or hepatitis E.",
    "If you received Ambirix\u00ae: hepatitis B protection is not fully established until after the second dose. Continue to use precautions against hepatitis B exposure between doses.",
    "If you received Twinrix Adult\u00ae on the accelerated schedule: the 12-month booster dose is essential for long-term protection, particularly against hepatitis B. Please ensure you return for this dose.",
    "Hepatitis A is transmitted through contaminated food and water. Follow food and water hygiene advice: drink bottled or boiled water, avoid ice in drinks, eat freshly cooked food, and wash hands before eating and after using the toilet.",
    "Hepatitis B is transmitted through blood-to-blood contact, sexual contact, and from mother to child. Continue to use appropriate precautions until the course is complete.",
    "If you develop symptoms of hepatitis A or B (jaundice, dark urine, extreme tiredness, or abdominal pain) seek medical advice promptly from your GP or NHS 111.",
  ],
});
